import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  ArrowRight,
  Calculator,
  ChartNoAxesColumnIncreasing,
  Check,
  CircleCheck,
  Clock,
  House,
  LockKeyhole,
  MessageSquareText,
  ShieldCheck,
  Target,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { EnquiryFormData, enquirySchema } from "@/model/schemas";
import EnquiryRequest from "@/model/interface/EnquiryRequest";
import { usePostEnquiryMutation } from "@/store/service";
import { zodFormResolver } from "@/util/form";
import { parseAmount } from "@/util/sums";
import { cn } from "@/lib/utils";
import Container from "../../site/Container";
import CtaLink, { ctaVariants } from "../../site/CtaLink";
import PageHeader from "../../site/PageHeader";
import { EnquiryField } from "./EnquiryFields";
import { DetailsStep, GoalsStep, ReviewStep, SituationStep } from "./EnquirySteps";

interface Assurance {
  icon: LucideIcon;
  title: string;
  text: string;
}

interface Step {
  label: string;
  title: string;
  lead: string;
  action: string;
  fields: EnquiryField[];
  assurances: Assurance[];
}

const steps: Step[] = [
  {
    label: "Your details",
    title: "Tell us about your home goals.",
    lead: "Complete this short form and we’ll let you know if you’re eligible for a BuySooner Boost.",
    action: "Continue",
    fields: ["firstName", "lastName", "email", "phone"],
    assurances: [
      {
        icon: LockKeyhole,
        title: "Your information is secure",
        text: "We protect your data and only use it to assess your application.",
      },
      { icon: Clock, title: "It takes about 3 minutes", text: "A few simple questions to get started." },
      {
        icon: MessageSquareText,
        title: "No obligation",
        text: "Checking your eligibility doesn’t commit you to anything.",
      },
    ],
  },
  {
    label: "Your situation",
    title: "Tell us about your situation.",
    lead: "A few details about where you are today help us understand your potential buying power.",
    action: "Continue",
    fields: ["buyingWith", "employmentStatus", "annualIncome", "depositSavings", "propertyOwnership", "monthlyDebts"],
    assurances: [
      {
        icon: Target,
        title: "Why we ask",
        text: "Your income, deposit and commitments help us assess whether a Boost may be suitable.",
      },
      { icon: Calculator, title: "Estimates are fine", text: "You can provide approximate figures at this stage." },
      {
        icon: ShieldCheck,
        title: "Private by design",
        text: "Your information is used to assess your enquiry, not shared publicly.",
      },
    ],
  },
  {
    label: "Your goals",
    title: "What does your next home look like?",
    lead: "Tell us what you’re hoping to buy, and when you’d like to make your move.",
    action: "Review & submit",
    fields: ["location", "targetPrice", "timeframe", "propertyType", "loanApproval", "priorities"],
    assurances: [
      {
        icon: House,
        title: "A home that fits",
        text: "We want to understand the home you actually want, not just your current budget.",
      },
      {
        icon: ChartNoAxesColumnIncreasing,
        title: "Understand your shortfall",
        text: "Your target price helps us estimate the additional buying power you may need.",
      },
      { icon: Clock, title: "No rush", text: "Your preferred timeframe helps us tailor the next steps." },
    ],
  },
  {
    label: "Submit",
    title: "Review your enquiry.",
    lead: "Check your details before sending them to the OnHome team.",
    action: "Submit application",
    fields: [],
    assurances: [
      {
        icon: CircleCheck,
        title: "Ready when you are",
        text: "Review your answers and submit when you’re comfortable.",
      },
      {
        icon: LockKeyhole,
        title: "Your privacy matters",
        text: "Your information will be handled in accordance with the applicable privacy policy.",
      },
      {
        icon: MessageSquareText,
        title: "What happens next",
        text: "The team will review your enquiry and explain the next steps. No eligibility or approval is guaranteed.",
      },
    ],
  },
];

const defaultValues: EnquiryFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  buyingWith: "",
  employmentStatus: "",
  annualIncome: "",
  depositSavings: "",
  propertyOwnership: "",
  monthlyDebts: "",
  location: "",
  targetPrice: "",
  timeframe: "",
  propertyType: "",
  loanApproval: "",
  priorities: "",
  confirmAccurate: false,
  agreePrivacy: false,
};

const toEnquiryRequest = (data: EnquiryFormData): EnquiryRequest => ({
  firstName: data.firstName.trim(),
  lastName: data.lastName.trim(),
  email: data.email.trim(),
  phone: data.phone.replace(/\s+/g, ""),
  buyingWith: data.buyingWith,
  employmentStatus: data.employmentStatus,
  annualIncome: parseAmount(data.annualIncome),
  depositSavings: parseAmount(data.depositSavings),
  propertyOwnership: data.propertyOwnership,
  monthlyDebts: parseAmount(data.monthlyDebts),
  location: data.location.trim(),
  targetPrice: parseAmount(data.targetPrice),
  timeframe: data.timeframe,
  propertyType: data.propertyType,
  loanApproval: parseAmount(data.loanApproval),
  priorities: data.priorities?.trim() || undefined,
  confirmAccurate: data.confirmAccurate,
  agreePrivacy: data.agreePrivacy,
});

const Stepper = ({ current, className }: { current: number; className?: string }) => {
  return (
    <ol className={cn("flex items-center", className)}>
      {steps.map(({ label }, index) => {
        const isDone = index < current;
        const isCurrent = index === current;

        return (
          <li
            key={label}
            aria-current={isCurrent ? "step" : undefined}
            className="flex flex-1 items-center gap-3 last:flex-none"
          >
            <span
              className={cn(
                "grid size-10 shrink-0 place-items-center rounded-full text-sm font-semibold",
                isDone || isCurrent ? "bg-brand text-white" : "bg-slate-100 text-slate-600",
              )}
            >
              {isDone ? <Check className="size-4" strokeWidth={3} aria-hidden /> : index + 1}
            </span>
            <span
              className={cn(
                "text-[15px] font-semibold whitespace-nowrap",
                isCurrent ? "text-brand-ink-strong" : "text-slate-500 max-sm:sr-only",
              )}
            >
              {label}
            </span>
            {index < steps.length - 1 && <span aria-hidden className="mx-1 h-0.5 flex-1 bg-[#e0e9ea]" />}
          </li>
        );
      })}
    </ol>
  );
};

const EnquirySent = ({ firstName }: { firstName: string }) => {
  return (
    <section className="py-14 sm:py-20">
      <Container>
        <PageHeader eyebrow="Enquiry sent" title={`Thanks, ${firstName}.`} size="compact">
          <p>We’ve received your enquiry. Our team will review it and contact you about the next steps.</p>
        </PageHeader>
        <CtaLink to="/" className="mt-8">
          Back to home
        </CtaLink>
      </Container>
    </section>
  );
};

const GetStarted = () => {
  const [step, setStep] = useState(0);
  const [postEnquiry, { isLoading, isSuccess }] = usePostEnquiryMutation();
  const form = useForm<EnquiryFormData>({
    resolver: zodFormResolver<EnquiryFormData>(enquirySchema),
    mode: "onTouched",
    defaultValues,
  });

  const current = steps[step];
  const isReview = step === steps.length - 1;

  const goTo = (index: number) => {
    setStep(index);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onContinue = async (event: React.FormEvent) => {
    event.preventDefault();
    if (await form.trigger(current.fields, { shouldFocus: true })) {
      goTo(step + 1);
    }
  };

  const onSubmit = async (data: EnquiryFormData) => {
    toast.dismiss();
    try {
      await postEnquiry(toEnquiryRequest(data)).unwrap();
    } catch {
      toast.error("We couldn’t send your enquiry. Please try again.");
    }
  };

  if (isSuccess) {
    return <EnquirySent firstName={form.getValues("firstName")} />;
  }

  return (
    <section className="py-14 sm:py-20">
      <Container>
        <PageHeader eyebrow={`Get started · Step ${step + 1} of ${steps.length}`} title={current.title} size="compact">
          <p>{current.lead}</p>
        </PageHeader>

        <Stepper current={step} className="mt-10" />

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.4fr_0.85fr]">
          <FormProvider {...form}>
            <form
              noValidate
              onSubmit={isReview ? form.handleSubmit(onSubmit) : onContinue}
              className="rounded-[18px] border border-[#e1e9e9] bg-white p-6 shadow-[0_7px_24px_rgba(7,46,64,0.06)] sm:p-10"
            >
              <h2 className="text-xl font-semibold text-navy">{current.label}</h2>
              <div className="mt-7">
                {step === 0 && <DetailsStep />}
                {step === 1 && <SituationStep />}
                {step === 2 && <GoalsStep />}
                {isReview && <ReviewStep onEdit={goTo} />}
              </div>
              <div className="mt-8 flex items-center justify-between gap-4">
                {step > 0 ? (
                  <button
                    type="button"
                    onClick={() => goTo(step - 1)}
                    className="inline-flex cursor-pointer items-center gap-2 text-[15px] font-semibold text-slate-500 transition-colors hover:text-navy"
                  >
                    <ArrowLeft className="size-4" aria-hidden />
                    Back
                  </button>
                ) : (
                  <span />
                )}
                <button type="submit" disabled={isLoading} className={ctaVariants()}>
                  {isLoading ? "Sending…" : current.action}
                  <ArrowRight className="size-4" aria-hidden />
                </button>
              </div>
            </form>
          </FormProvider>

          <aside className="rounded-[18px] bg-mint p-7 sm:p-9">
            <ul className="space-y-10">
              {current.assurances.map(({ icon: Icon, title, text }) => (
                <li key={title} className="flex gap-4">
                  <Icon className="mt-0.5 size-7 shrink-0 text-brand-ink" strokeWidth={1.75} aria-hidden />
                  <div>
                    <h3 className="font-semibold text-navy">{title}</h3>
                    <p className="mt-1 text-[15px] leading-relaxed text-slate-600">{text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </Container>
    </section>
  );
};

export default GetStarted;
